import { type ReactNode, useId, useState } from 'react';
import { toast } from 'sonner';

import {
  bandFromSignUpSize,
  canAnswer,
  type CompanyProfileRequestApi,
  type CompanySizeBand,
  companySizeOptions,
  headcountLabel,
  neededPicks,
  profileAnswerRequest,
} from '@/modules/companies/companyProfile';
import { useAnswerCompanyProfileMutation } from '@/modules/companies/mutations/useAnswerCompanyProfileMutation';
import { useCompanyLookupQuery } from '@/modules/companies/queries/useCompanyLookupQuery';
import { useIndustriesQuery } from '@/modules/companies/queries/useIndustriesQuery';
import { Button } from '@/modules/ui/components/button/Button';
import { Combobox } from '@/modules/ui/components/combobox/Combobox';
import { Label } from '@/modules/ui/components/label/Label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/modules/ui/components/radio-group/RadioGroup';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

interface CompanyProfileCheckProps {
  companyId: string;
  /** The size given at sign-up, which pre-selects the band it gives. */
  signUpSize?: string | null;
  /** Called once the answer is kept, or skipped. */
  onDone: () => void;
  /** Offers Skip, which keeps only that it was asked: the one-time screen at sign-in. */
  allowSkip?: boolean;
}

/**
 * "Is this your company?": the company Coresignal has for the company's website, with its headcount and industry,
 * which the company's new jobs take their suggestions from. A yes can correct the headcount, the industry or both;
 * a no, or nothing found, asks for both. The size is one of three bands, and the industry a search of Coresignal's
 * names that finds them from partial words or missing letters ("softw dev", "sofware").
 */
export function CompanyProfileCheck({
  companyId,
  signUpSize,
  onDone,
  allowSkip = false,
}: CompanyProfileCheckProps) {
  const lookupQuery = useCompanyLookupQuery(companyId);
  const answerMutation = useAnswerCompanyProfileMutation();
  const [rejected, setRejected] = useState(false);
  const [marked, setMarked] = useState({ size: false, industry: false });
  const [sizeBand, setSizeBand] = useState(bandFromSignUpSize(signUpSize));
  const [industry, setIndustry] = useState<string>();

  if (lookupQuery.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <p className="text-muted-foreground text-sm">
          Looking up your company…
        </p>
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  // A failed lookup is treated as nothing found: the user picks the size and industry themselves.
  const found = lookupQuery.data ?? null;
  const draft = {
    isOurCompany: found !== null && !rejected,
    sizeBand,
    industry,
  };
  const needed = neededPicks(draft, found, marked);
  const ready = canAnswer(draft, needed);

  const answer = async (body: CompanyProfileRequestApi) => {
    try {
      await answerMutation.mutateAsync({ companyId, ...body });
      onDone();
    } catch {
      toast.error('Saving your company details failed. Please try again.');
    }
  };

  const sizePicker = (
    <SizePicker value={sizeBand} onChange={(band) => setSizeBand(band)} />
  );
  const industryPicker = (
    <IndustryPicker value={industry} onChange={(name) => setIndustry(name)} />
  );

  return (
    <div className="flex flex-col gap-6">
      {draft.isOurCompany && found ? (
        <section className="flex flex-col gap-4">
          <p className="font-medium">Is this your company?</p>
          <div className="flex flex-col gap-4 rounded-lg border p-4">
            <div>
              <p className="font-medium">{found.name}</p>
              {found.website && (
                <p className="text-muted-foreground text-sm">
                  {displayWebsite(found.website)}
                </p>
              )}
            </div>
            <Fact
              label="Size"
              value={headcountLabel(found)}
              canMarkWrong={!needed.size}
              onMarkWrong={() => setMarked({ ...marked, size: true })}
            >
              {needed.size && sizePicker}
            </Fact>
            <Fact
              label="Industry"
              value={found.industry ?? undefined}
              canMarkWrong={!needed.industry}
              onMarkWrong={() => setMarked({ ...marked, industry: true })}
            >
              {needed.industry && industryPicker}
            </Fact>
          </div>
        </section>
      ) : (
        <section className="flex flex-col gap-4">
          <p className="font-medium">
            {found
              ? 'Tell us about your company instead.'
              : 'We couldn’t find your company from its website. Tell us about it:'}
          </p>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Size</p>
            {sizePicker}
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Industry</p>
            {industryPicker}
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={!ready}
          loading={answerMutation.isPending}
          onClick={() => void answer(profileAnswerRequest(draft, needed))}
        >
          {draft.isOurCompany ? 'Yes, that’s us' : 'Continue'}
        </Button>
        {found && draft.isOurCompany && (
          <Button variant="outline" onClick={() => setRejected(true)}>
            No, that’s not us
          </Button>
        )}
        {found && !draft.isOurCompany && (
          <Button variant="ghost" onClick={() => setRejected(false)}>
            Back
          </Button>
        )}
        {allowSkip && (
          <Button
            variant="ghost"
            disabled={answerMutation.isPending}
            onClick={() => void answer({ skipped: true })}
          >
            Skip
          </Button>
        )}
      </div>
    </div>
  );
}

interface FactProps {
  label: string;
  value: string | undefined;
  /** Whether "Not right?" is offered: there's a value, and the user hasn't marked it wrong yet. */
  canMarkWrong: boolean;
  onMarkWrong: () => void;
  /** The picker, once the value is marked wrong or Coresignal has none. */
  children: ReactNode;
}

/** One fact Coresignal has about the company, which the user can mark wrong and replace from the choices. */
function Fact({
  label,
  value,
  canMarkWrong,
  onMarkWrong,
  children,
}: FactProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span>
          <span className="text-muted-foreground">{label}: </span>
          {value ?? 'not known'}
        </span>
        {value && canMarkWrong && (
          <Button
            variant="link"
            size="sm"
            className="px-0"
            aria-label={`${label}: not right?`}
            onClick={onMarkWrong}
          >
            Not right?
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

function SizePicker({
  value,
  onChange,
}: {
  value: CompanySizeBand | undefined;
  onChange: (band: CompanySizeBand) => void;
}) {
  const id = useId();
  return (
    <RadioGroup
      aria-label="Company size"
      value={value ?? ''}
      onValueChange={(next) => {
        const band = companySizeOptions.find((option) => option.value === next);
        if (band) onChange(band.value);
      }}
    >
      {companySizeOptions.map((option) => (
        <div key={option.value} className="flex items-center gap-2">
          <RadioGroupItem value={option.value} id={`${id}-${option.value}`} />
          <Label htmlFor={`${id}-${option.value}`}>{option.label}</Label>
        </div>
      ))}
    </RadioGroup>
  );
}

function IndustryPicker({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (industry: string) => void;
}) {
  const industriesQuery = useIndustriesQuery();
  const options = (industriesQuery.data ?? []).map((name) => ({
    value: name,
    label: name,
  }));

  return (
    <Combobox
      aria-label="Industry"
      value={value}
      options={options}
      disableSort
      disabled={industriesQuery.isPending}
      placeholder={
        industriesQuery.isPending ? 'Loading industries…' : 'Pick an industry'
      }
      searchPlaceholder="Search, e.g. software"
      emptyMessage="No industry by that name."
      onChange={onChange}
    />
  );
}

/** "https://www.finlay.ai/" as "finlay.ai". */
const displayWebsite = (website: string) =>
  website
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
